import React, { useState, useEffect } from 'react';
import { 
  UserCheck, 
  Search, 
  Plus, 
  Edit, 
  Eye, 
  CheckCircle, 
  XCircle,
  Mail,
  Phone,
  MapPin,
  Download,
  Star,
  FileText,
  Shield,
  Clock,
  Play,
  Globe,
  Upload,
  X
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';

interface PortfolioItem {
  id: number;
  title: string;
  description?: string;
  type: 'image' | 'video' | 'document' | 'link';
  url: string;
  thumbnailUrl?: string;
  category?: string;
  tags?: string[];
  displayOrder: number;
  status: 'active' | 'inactive';
}

interface ServiceProvider {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  parish: string;
  profilePicture?: string | null;
  coverImage?: string | null;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  bio?: string;
  experienceYears: number;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  profileStatus: 'active' | 'inactive' | 'suspended';
  rating: number;
  totalReviews: number;
  totalJobsCompleted: number;
  subscriptionPlan?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  skills: string[];
  portfolioItems?: PortfolioItem[];
  availabilityHours?: {
    [key: string]: { start: string; end: string }[];
  };
  certifications?: {
    name: string;
    issuer: string;
    date: string;
    url?: string;
  }[];
  languages?: {
    language: string;
    proficiency: 'basic' | 'intermediate' | 'fluent' | 'native';
  }[];
  socialMediaLinks?: {
    platform: string;
    url: string;
  }[];
  idProof?: string;
  lastActive: string;
  createdAt: string;
  documents: {
    idProof?: string;
    portfolio?: string[];
    certifications?: string[];
  };
}

interface ServiceProviderFormData extends Omit<ServiceProvider, 'id' | 'lastActive' | 'createdAt'> {
  coverImageFile?: File;
  profilePictureFile?: File;
  newPortfolioItems: {
    file?: File;
    title: string;
    description?: string;
    type: 'image' | 'video' | 'document' | 'link';
    url?: string;
    category?: string;
    tags: string[];
  }[];
  availabilityHours: {
    [key: string]: { start: string; end: string }[];
  };
  certifications: {
    name: string;
    issuer: string;
    date: string;
    url?: string;
  }[];
  languages: {
    language: string;
    proficiency: 'basic' | 'intermediate' | 'fluent' | 'native';
  }[];
  socialMediaLinks: {
    platform: string;
    url: string;
  }[];
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const LANGUAGE_PROFICIENCY_LEVELS = ['basic', 'intermediate', 'fluent', 'native'];
const SOCIAL_MEDIA_PLATFORMS = ['Facebook', 'Twitter', 'Instagram', 'LinkedIn', 'YouTube', 'Website'];

const ServiceProviderForm: React.FC<{
  provider?: ServiceProvider;
  onSubmit: (data: ServiceProviderFormData) => void;
  onClose: () => void;
}> = ({ provider, onSubmit, onClose }) => {
  const [formData, setFormData] = useState<ServiceProviderFormData>(() => ({
    firstName: provider?.firstName || '',
    lastName: provider?.lastName || '',
    email: provider?.email || '',
    phone: provider?.phone || '',
    address: provider?.address || '',
    parish: provider?.parish || '',
    profilePicture: provider?.profilePicture || null,
    coverImage: provider?.coverImage || null,
    dateOfBirth: provider?.dateOfBirth || '',
    gender: provider?.gender || undefined,
    bio: provider?.bio || '',
    experienceYears: provider?.experienceYears || 0,
    verificationStatus: provider?.verificationStatus || 'pending',
    profileStatus: provider?.profileStatus || 'active',
    skills: provider?.skills || [],
    portfolioItems: provider?.portfolioItems || [],
    newPortfolioItems: [],
    availabilityHours: provider?.availabilityHours || {},
    certifications: provider?.certifications || [],
    languages: provider?.languages || [],
    socialMediaLinks: provider?.socialMediaLinks || [],
    documents: provider?.documents || { idProof: undefined, portfolio: [], certifications: [] },
    rating: provider?.rating || 0,
    totalReviews: provider?.totalReviews || 0,
    totalJobsCompleted: provider?.totalJobsCompleted || 0
  }));

  const handleInputChange = (field: keyof ServiceProviderFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (field: 'coverImageFile' | 'profilePictureFile', file: File) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const handlePortfolioItemAdd = () => {
    setFormData(prev => ({
      ...prev,
      newPortfolioItems: [
        ...prev.newPortfolioItems,
        {
          title: '',
          type: 'image',
          tags: []
        }
      ]
    }));
  };

  const handlePortfolioItemUpdate = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      newPortfolioItems: prev.newPortfolioItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleAvailabilityUpdate = (day: string, index: number, field: 'start' | 'end', value: string) => {
    setFormData(prev => ({
      ...prev,
      availabilityHours: {
        ...prev.availabilityHours,
        [day]: (prev.availabilityHours[day] || []).map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const handleAvailabilityAdd = (day: string) => {
    setFormData(prev => ({
      ...prev,
      availabilityHours: {
        ...prev.availabilityHours,
        [day]: [...(prev.availabilityHours[day] || []), { start: '09:00', end: '17:00' }]
      }
    }));
  };

  const handleCertificationAdd = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        { name: '', issuer: '', date: '', url: '' }
      ]
    }));
  };

  const handleLanguageAdd = () => {
    setFormData(prev => ({
      ...prev,
      languages: [
        ...prev.languages,
        { language: '', proficiency: 'intermediate' }
      ]
    }));
  };

  const handleSocialMediaAdd = () => {
    setFormData(prev => ({
      ...prev,
      socialMediaLinks: [
        ...prev.socialMediaLinks,
        { platform: '', url: '' }
      ]
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '800px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>{provider ? 'Edit Service Provider' : 'New Service Provider'}</h2>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            onSubmit(formData);
          }}>
            {/* Basic Information */}
            <section className="form-section">
              <h3>Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>
              </div>
            </section>

            {/* Profile Media */}
            <section className="form-section">
              <h3>Profile Media</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Profile Picture</label>
                  <div className="file-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files && handleFileUpload('profilePictureFile', e.target.files[0])}
                    />
                    <Upload size={20} />
                    <span>Choose file</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Cover Image</label>
                  <div className="file-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files && handleFileUpload('coverImageFile', e.target.files[0])}
                    />
                    <Upload size={20} />
                    <span>Choose file</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Location & Bio */}
            <section className="form-section">
              <h3>Location & Bio</h3>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.parish}
                  onChange={(e) => handleInputChange('parish', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={4}
                />
              </div>
            </section>

            {/* Skills & Experience */}
            <section className="form-section">
              <h3>Skills & Experience</h3>
              <div className="form-group">
                <label>Skills (comma-separated)</label>
                <input
                  type="text"
                  value={formData.skills.join(', ')}
                  onChange={(e) => handleInputChange('skills', e.target.value.split(',').map(s => s.trim()))}
                />
              </div>
              <div className="form-group">
                <label>Years of Experience</label>
                <input
                  type="number"
                  value={formData.experienceYears}
                  onChange={(e) => handleInputChange('experienceYears', parseInt(e.target.value))}
                  min="0"
                />
              </div>
            </section>

            {/* Portfolio */}
            <section className="form-section">
              <div className="section-header">
                <h3>Portfolio</h3>
                <button type="button" className="btn-secondary" onClick={handlePortfolioItemAdd}>
                  <Plus size={16} />
                  Add Item
                </button>
              </div>
              {formData.newPortfolioItems.map((item, index) => (
                <div key={index} className="portfolio-item-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handlePortfolioItemUpdate(index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        value={item.type}
                        onChange={(e) => handlePortfolioItemUpdate(index, 'type', e.target.value)}
                      >
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                        <option value="document">Document</option>
                        <option value="link">Link</option>
                      </select>
                    </div>
                    {item.type === 'link' ? (
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="url"
                          value={item.url}
                          onChange={(e) => handlePortfolioItemUpdate(index, 'url', e.target.value)}
                        />
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>File</label>
                        <div className="file-upload">
                          <input
                            type="file"
                            accept={item.type === 'image' ? 'image/*' : item.type === 'video' ? 'video/*' : '*/*'}
                            onChange={(e) => e.target.files && handlePortfolioItemUpdate(index, 'file', e.target.files[0])}
                          />
                          <Upload size={20} />
                          <span>Choose file</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={item.description}
                      onChange={(e) => handlePortfolioItemUpdate(index, 'description', e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={item.tags.join(', ')}
                      onChange={(e) => handlePortfolioItemUpdate(index, 'tags', e.target.value.split(',').map(t => t.trim()))}
                    />
                  </div>
                </div>
              ))}
            </section>

            {/* Availability */}
            <section className="form-section">
              <h3>Availability</h3>
              {DAYS_OF_WEEK.map(day => (
                <div key={day} className="availability-day">
                  <div className="day-header">
                    <h4>{day}</h4>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleAvailabilityAdd(day)}
                    >
                      <Plus size={16} />
                      Add Time Slot
                    </button>
                  </div>
                  {(formData.availabilityHours[day] || []).map((slot, index) => (
                    <div key={index} className="time-slot">
                      <input
                        type="time"
                        value={slot.start}
                        onChange={(e) => handleAvailabilityUpdate(day, index, 'start', e.target.value)}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={slot.end}
                        onChange={(e) => handleAvailabilityUpdate(day, index, 'end', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </section>

            {/* Certifications */}
            <section className="form-section">
              <div className="section-header">
                <h3>Certifications</h3>
                <button type="button" className="btn-secondary" onClick={handleCertificationAdd}>
                  <Plus size={16} />
                  Add Certification
                </button>
              </div>
              {formData.certifications.map((cert, index) => (
                <div key={index} className="certification-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={cert.name}
                        onChange={(e) => {
                          const newCerts = [...formData.certifications];
                          newCerts[index] = { ...cert, name: e.target.value };
                          handleInputChange('certifications', newCerts);
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Issuer</label>
                      <input
                        type="text"
                        value={cert.issuer}
                        onChange={(e) => {
                          const newCerts = [...formData.certifications];
                          newCerts[index] = { ...cert, issuer: e.target.value };
                          handleInputChange('certifications', newCerts);
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input
                        type="date"
                        value={cert.date}
                        onChange={(e) => {
                          const newCerts = [...formData.certifications];
                          newCerts[index] = { ...cert, date: e.target.value };
                          handleInputChange('certifications', newCerts);
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>URL (optional)</label>
                      <input
                        type="url"
                        value={cert.url}
                        onChange={(e) => {
                          const newCerts = [...formData.certifications];
                          newCerts[index] = { ...cert, url: e.target.value };
                          handleInputChange('certifications', newCerts);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* Languages */}
            <section className="form-section">
              <div className="section-header">
                <h3>Languages</h3>
                <button type="button" className="btn-secondary" onClick={handleLanguageAdd}>
                  <Plus size={16} />
                  Add Language
                </button>
              </div>
              {formData.languages.map((lang, index) => (
                <div key={index} className="language-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Language</label>
                      <input
                        type="text"
                        value={lang.language}
                        onChange={(e) => {
                          const newLangs = [...formData.languages];
                          newLangs[index] = { ...lang, language: e.target.value };
                          handleInputChange('languages', newLangs);
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Proficiency</label>
                      <select
                        value={lang.proficiency}
                        onChange={(e) => {
                          const newLangs = [...formData.languages];
                          newLangs[index] = { ...lang, proficiency: e.target.value as any };
                          handleInputChange('languages', newLangs);
                        }}
                      >
                        {LANGUAGE_PROFICIENCY_LEVELS.map(level => (
                          <option key={level} value={level}>
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* Social Media */}
            <section className="form-section">
              <div className="section-header">
                <h3>Social Media</h3>
                <button type="button" className="btn-secondary" onClick={handleSocialMediaAdd}>
                  <Plus size={16} />
                  Add Social Media
                </button>
              </div>
              {formData.socialMediaLinks.map((social, index) => (
                <div key={index} className="social-media-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Platform</label>
                      <select
                        value={social.platform}
                        onChange={(e) => {
                          const newSocial = [...formData.socialMediaLinks];
                          newSocial[index] = { ...social, platform: e.target.value };
                          handleInputChange('socialMediaLinks', newSocial);
                        }}
                      >
                        <option value="">Select Platform</option>
                        {SOCIAL_MEDIA_PLATFORMS.map(platform => (
                          <option key={platform} value={platform}>{platform}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>URL</label>
                      <input
                        type="url"
                        value={social.url}
                        onChange={(e) => {
                          const newSocial = [...formData.socialMediaLinks];
                          newSocial[index] = { ...social, url: e.target.value };
                          handleInputChange('socialMediaLinks', newSocial);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {provider ? 'Save Changes' : 'Create Provider'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ServiceProviderManagement: React.FC = () => {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [skillFilter, setSkillFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    setTimeout(() => {
      setProviders([
        {
          id: 1,
          firstName: 'Mike',
          lastName: 'Johnson',
          email: 'mike.johnson@email.com',
          phone: '+1234567890',
          address: '123 Worker St, Downtown',
          parish: 'Kingston',
          profilePicture: null,
          dateOfBirth: '1985-03-15',
          gender: 'male',
          bio: 'Experienced plumber with 10+ years in residential and commercial plumbing.',
          experienceYears: 12,
          verificationStatus: 'verified',
          profileStatus: 'active',
          rating: 4.8,
          totalReviews: 127,
          totalJobsCompleted: 89,
          subscriptionPlan: 'Premium',
          subscriptionStartDate: '2024-01-01',
          subscriptionEndDate: '2024-12-31',
          skills: ['Plumbing', 'Pipe Repair', 'Installation'],
          portfolioItems: [
            {
              id: 1,
              title: 'Plumbing Project 1',
              description: 'A detailed description of the plumbing project',
              type: 'image',
              url: 'https://example.com/image1.jpg',
              thumbnailUrl: 'https://example.com/thumbnail1.jpg',
              category: 'Plumbing',
              tags: ['Residential', 'Commercial'],
              displayOrder: 1,
              status: 'active'
            },
            {
              id: 2,
              title: 'Plumbing Project 2',
              description: 'A detailed description of the plumbing project',
              type: 'video',
              url: 'https://example.com/video1.mp4',
              thumbnailUrl: 'https://example.com/thumbnail2.jpg',
              category: 'Plumbing',
              tags: ['Residential', 'Commercial'],
              displayOrder: 2,
              status: 'active'
            }
          ],
          availabilityHours: {
            Monday: [{ start: '09:00', end: '17:00' }],
            Wednesday: [{ start: '09:00', end: '17:00' }],
            Friday: [{ start: '09:00', end: '17:00' }]
          },
          certifications: [
            {
              name: 'Plumbing Certification',
              issuer: 'Local Plumbing Association',
              date: '2024-01-01',
              url: 'https://example.com/plumbing_cert.pdf'
            }
          ],
          languages: [
            {
              language: 'English',
              proficiency: 'fluent'
            },
            {
              language: 'Spanish',
              proficiency: 'intermediate'
            }
          ],
          socialMediaLinks: [
            {
              platform: 'Facebook',
              url: 'https://facebook.com/mikejohnson'
            },
            {
              platform: 'Twitter',
              url: 'https://twitter.com/mikejohnson'
            }
          ],
          idProof: 'verified',
          lastActive: '2 hours ago',
          createdAt: '2023-12-15',
          documents: {
            idProof: 'id_proof_1.pdf',
            portfolio: ['work1.jpg', 'work2.jpg'],
            certifications: ['plumbing_cert.pdf']
          }
        },
        {
          id: 2,
          firstName: 'Sarah',
          lastName: 'Williams',
          email: 'sarah.w@email.com',
          phone: '+1234567891',
          address: '456 Service Ave, Uptown',
          parish: 'St. Andrew',
          dateOfBirth: '1990-07-22',
          gender: 'female',
          bio: 'Professional cleaner specializing in residential and office cleaning services.',
          experienceYears: 5,
          verificationStatus: 'pending',
          profileStatus: 'inactive',
          rating: 0,
          totalReviews: 0,
          totalJobsCompleted: 0,
          skills: ['House Cleaning', 'Office Cleaning', 'Deep Cleaning'],
          portfolioItems: [],
          availabilityHours: {
            Tuesday: [{ start: '09:00', end: '17:00' }],
            Thursday: [{ start: '09:00', end: '17:00' }]
          },
          certifications: [],
          languages: [],
          socialMediaLinks: [],
          lastActive: '1 day ago',
          createdAt: '2024-01-18',
          documents: {
            idProof: 'id_proof_2.pdf',
            portfolio: ['cleaning1.jpg']
          }
        },
        {
          id: 3,
          firstName: 'David',
          lastName: 'Martinez',
          email: 'david.m@email.com',
          phone: '+1234567892',
          address: '789 Tech Lane, Midtown',
          parish: 'Spanish Town',
          dateOfBirth: '1988-11-10',
          gender: 'male',
          bio: 'Certified electrician with expertise in residential wiring and electrical repairs.',
          experienceYears: 8,
          verificationStatus: 'verified',
          profileStatus: 'active',
          rating: 4.6,
          totalReviews: 73,
          totalJobsCompleted: 45,
          subscriptionPlan: 'Basic',
          subscriptionStartDate: '2024-01-10',
          subscriptionEndDate: '2024-07-10',
          skills: ['Electrical Repair', 'Wiring', 'Installation'],
          portfolioItems: [
            {
              id: 3,
              title: 'Electrical Project 1',
              description: 'A detailed description of the electrical project',
              type: 'image',
              url: 'https://example.com/image2.jpg',
              thumbnailUrl: 'https://example.com/thumbnail3.jpg',
              category: 'Electrical',
              tags: ['Residential'],
              displayOrder: 1,
              status: 'active'
            },
            {
              id: 4,
              title: 'Electrical Project 2',
              description: 'A detailed description of the electrical project',
              type: 'video',
              url: 'https://example.com/video2.mp4',
              thumbnailUrl: 'https://example.com/thumbnail4.jpg',
              category: 'Electrical',
              tags: ['Residential'],
              displayOrder: 2,
              status: 'active'
            }
          ],
          availabilityHours: {
            Monday: [{ start: '09:00', end: '17:00' }],
            Wednesday: [{ start: '09:00', end: '17:00' }],
            Friday: [{ start: '09:00', end: '17:00' }]
          },
          certifications: [
            {
              name: 'Electrical License',
              issuer: 'National Electrical Board',
              date: '2024-01-01',
              url: 'https://example.com/electrical_license.pdf'
            },
            {
              name: 'Safety Certificate',
              issuer: 'Local Electrical Association',
              date: '2024-01-01',
              url: 'https://example.com/safety_cert.pdf'
            }
          ],
          languages: [
            {
              language: 'English',
              proficiency: 'fluent'
            },
            {
              language: 'Spanish',
              proficiency: 'intermediate'
            }
          ],
          socialMediaLinks: [
            {
              platform: 'Facebook',
              url: 'https://facebook.com/davidmartinez'
            },
            {
              platform: 'Twitter',
              url: 'https://twitter.com/davidmartinez'
            }
          ],
          lastActive: '30 minutes ago',
          createdAt: '2023-11-20',
          documents: {
            idProof: 'id_proof_3.pdf',
            certifications: ['electrical_license.pdf', 'safety_cert.pdf']
          }
        },
        {
          id: 4,
          firstName: 'Lisa',
          lastName: 'Chen',
          email: 'lisa.chen@email.com',
          phone: '+1234567893',
          address: '321 Garden St, Suburbia',
          parish: 'St. Catherine',
          dateOfBirth: '1992-05-03',
          gender: 'female',
          bio: 'Passionate gardener offering lawn care, landscaping, and garden maintenance.',
          experienceYears: 3,
          verificationStatus: 'rejected',
          profileStatus: 'inactive',
          rating: 0,
          totalReviews: 0,
          totalJobsCompleted: 0,
          skills: ['Gardening', 'Landscaping', 'Lawn Care'],
          portfolioItems: [],
          availabilityHours: {
            Wednesday: [{ start: '09:00', end: '17:00' }],
            Friday: [{ start: '09:00', end: '17:00' }]
          },
          certifications: [],
          languages: [],
          socialMediaLinks: [],
          lastActive: '3 days ago',
          createdAt: '2024-01-10',
          documents: {
            idProof: 'id_proof_4.pdf'
          }
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = 
      provider.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.phone.includes(searchTerm) ||
      provider.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesVerification = verificationFilter === 'all' || provider.verificationStatus === verificationFilter;
    const matchesStatus = statusFilter === 'all' || provider.profileStatus === statusFilter;
    const matchesSkill = skillFilter === 'all' || provider.skills.some(skill => skill.toLowerCase().includes(skillFilter.toLowerCase()));
    
    return matchesSearch && matchesVerification && matchesStatus && matchesSkill;
  });

  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);
  const currentProviders = filteredProviders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectProvider = (providerId: number) => {
    setSelectedProviders(prev => 
      prev.includes(providerId) 
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProviders.length === currentProviders.length) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders(currentProviders.map(p => p.id));
    }
  };

  const handleAction = (provider: ServiceProvider, action: 'view' | 'edit' | 'approve' | 'reject' | 'documents') => {
    setSelectedProvider(provider);
    if (action === 'edit') {
      setShowForm(true);
    } else {
      // For other actions like view, documents, etc., you can implement separate modals
      console.log(`Action ${action} for provider:`, provider);
    }
  };

  const handleVerificationUpdate = (providerId: number, status: 'verified' | 'rejected') => {
    setProviders(prev => prev.map(provider => 
      provider.id === providerId 
        ? { 
            ...provider, 
            verificationStatus: status,
            profileStatus: status === 'verified' ? 'active' : 'inactive'
          }
        : provider
    ));
  };

  const handleStatusUpdate = (providerId: number, status: 'active' | 'inactive' | 'suspended') => {
    setProviders(prev => prev.map(provider => 
      provider.id === providerId 
        ? { ...provider, profileStatus: status }
        : provider
    ));
  };

  const exportProviders = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Skills', 'Experience', 'Verification', 'Status', 'Rating', 'Jobs'].join(','),
      ...filteredProviders.map(provider => [
        `${provider.firstName} ${provider.lastName}`,
        provider.email,
        provider.phone,
        provider.skills.join('; '),
        `${provider.experienceYears} years`,
        provider.verificationStatus,
        provider.profileStatus,
        provider.rating,
        provider.totalJobsCompleted
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'service_providers.csv';
    a.click();
  };

  const getVerificationBadge = (status: string) => {
    const colors = {
      pending: 'badge-warning',
      verified: 'badge-success',
      rejected: 'badge-danger'
    };
    return `badge ${colors[status as keyof typeof colors] || 'badge-secondary'}`;
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'badge-success',
      inactive: 'badge-warning',
      suspended: 'badge-danger'
    };
    return `badge ${colors[status as keyof typeof colors] || 'badge-secondary'}`;
  };

  const getSubscriptionBadge = (plan?: string) => {
    if (!plan) return 'badge badge-secondary';
    const colors = {
      basic: 'badge-info',
      premium: 'badge-success',
      pro: 'badge-warning'
    };
    return `badge ${colors[plan.toLowerCase() as keyof typeof colors] || 'badge-secondary'}`;
  };

  const handlePortfolioAction = (provider: ServiceProvider, item?: PortfolioItem) => {
    setSelectedProvider(provider);
    // For now, just log the portfolio action - you can implement a portfolio modal later
    console.log('Portfolio action for provider:', provider, 'item:', item);
  };

  const handleFormSubmit = async (data: ServiceProviderFormData) => {
    try {
      // TODO: Implement form submission logic
      console.log('Form data:', data);
      setShowForm(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Service Provider Management" subtitle="Manage and verify service providers">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  // Calculate statistics
  const stats = {
    total: providers.length,
    verified: providers.filter(p => p.verificationStatus === 'verified').length,
    pending: providers.filter(p => p.verificationStatus === 'pending').length,
    active: providers.filter(p => p.profileStatus === 'active').length,
    avgRating: providers.filter(p => p.rating > 0).reduce((sum, p) => sum + p.rating, 0) / providers.filter(p => p.rating > 0).length || 0
  };

  return (
    <AdminLayout title="Service Provider Management" subtitle="Manage and verify service providers">
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
              {/* Search */}
              <div style={{ position: 'relative', minWidth: '300px' }}>
                <input
                  type="text"
                  placeholder="Search providers..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                />
                <Search size={18} style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dark-gray)'
                }} />
              </div>

              {/* Filters */}
              <select 
                className="form-control" 
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                style={{ minWidth: '140px' }}
              >
                <option value="all">All Verification</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>

              <select 
                className="form-control" 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>

              <input
                type="text"
                placeholder="Filter by skill..."
                className="form-control"
                value={skillFilter}
                onChange={(e) => setSkillFilter(e.target.value)}
                style={{ minWidth: '150px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-secondary" onClick={exportProviders}>
                <Download size={18} />
                Export
              </button>
              <button className="btn-primary" onClick={() => {
                setSelectedProvider(null);
                setShowForm(true);
              }}>
                <Plus size={18} />
                Add Provider
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stat-cards" style={{ marginBottom: '25px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <UserCheck size={24} />
            </div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Providers</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Shield size={24} />
            </div>
            <div className="stat-value">{stats.verified}</div>
            <div className="stat-label">Verified</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Clock size={24} />
            </div>
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending Approval</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Active Providers</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Star size={24} />
            </div>
            <div className="stat-value">{stats.avgRating.toFixed(1)}</div>
            <div className="stat-label">Average Rating</div>
          </div>
        </div>

        {/* Provider Profile Card */}
        {selectedProvider && (
          <div className="admin-card profile-card">
            {/* Cover Image */}
            <div 
              className="cover-image" 
              style={{ 
                backgroundImage: selectedProvider.coverImage 
                  ? `url(${selectedProvider.coverImage})` 
                  : 'linear-gradient(to right, var(--primary-blue), var(--secondary-blue))',
                height: '200px',
                borderRadius: '8px 8px 0 0',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative'
              }}
            >
              {/* Profile Picture */}
              <div 
                className="profile-picture"
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  border: '4px solid white',
                  position: 'absolute',
                  bottom: '-60px',
                  left: '40px',
                  backgroundImage: selectedProvider.profilePicture 
                    ? `url(${selectedProvider.profilePicture})`
                    : 'none',
                  backgroundColor: 'var(--accent-blue)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '36px',
                  fontWeight: '600'
                }}
              >
                {!selectedProvider.profilePicture && 
                  `${selectedProvider.firstName[0]}${selectedProvider.lastName[0]}`}
              </div>
            </div>

            {/* Profile Info */}
            <div style={{ padding: '80px 40px 40px' }}>
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ marginBottom: '10px' }}>
                  {selectedProvider.firstName} {selectedProvider.lastName}
                </h2>
                <div style={{ display: 'flex', gap: '20px', color: 'var(--text-medium)' }}>
                  <div>
                    <Mail size={16} style={{ marginRight: '8px' }} />
                    {selectedProvider.email}
                  </div>
                  <div>
                    <Phone size={16} style={{ marginRight: '8px' }} />
                    {selectedProvider.phone}
                  </div>
                  <div>
                    <MapPin size={16} style={{ marginRight: '8px' }} />
                    {selectedProvider.parish}
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '10px' }}>About</h3>
                <p style={{ color: 'var(--text-medium)' }}>{selectedProvider.bio}</p>
              </div>

              {/* Skills & Experience */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px' }}>Skills & Experience</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {selectedProvider.skills.map((skill, index) => (
                    <span 
                      key={index}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        backgroundColor: 'var(--accent-blue-light)',
                        color: 'var(--accent-blue)',
                        fontSize: '14px'
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Portfolio */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Portfolio</h3>
                  <button 
                    className="btn-secondary"
                    onClick={() => handlePortfolioAction(selectedProvider)}
                  >
                    <Plus size={16} />
                    Add Portfolio Item
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
                  {selectedProvider.portfolioItems?.map((item) => (
                    <div 
                      key={item.id}
                      className="portfolio-item"
                      style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid var(--border-light)',
                        cursor: 'pointer'
                      }}
                      onClick={() => handlePortfolioAction(selectedProvider, item)}
                    >
                      {item.type === 'image' && (
                        <img 
                          src={item.url} 
                          alt={item.title}
                          style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                        />
                      )}
                      {item.type === 'video' && (
                        <div style={{ 
                          width: '100%', 
                          height: '150px', 
                          backgroundColor: 'var(--accent-dark)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Play size={40} color="white" />
                        </div>
                      )}
                      <div style={{ padding: '10px' }}>
                        <div style={{ fontWeight: '500', marginBottom: '5px' }}>{item.title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium)' }}>
                          {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px' }}>Certifications</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {selectedProvider.certifications?.map((cert, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        backgroundColor: 'var(--background-light)'
                      }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '5px' }}>{cert.name}</div>
                      <div style={{ fontSize: '14px', color: 'var(--text-medium)' }}>
                        {cert.issuer} • {new Date(cert.date).toLocaleDateString()}
                      </div>
                      {cert.url && (
                        <a 
                          href={cert.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-link"
                          style={{ marginTop: '10px', fontSize: '14px' }}
                        >
                          View Certificate
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px' }}>Languages</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  {selectedProvider.languages?.map((lang, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '10px 15px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        backgroundColor: 'var(--background-light)'
                      }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '2px' }}>{lang.language}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-medium)' }}>
                        {lang.proficiency.charAt(0).toUpperCase() + lang.proficiency.slice(1)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Availability Hours */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px' }}>Availability</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                  {Object.entries(selectedProvider.availabilityHours || {}).map(([day, hours]) => (
                    <div 
                      key={day}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        backgroundColor: 'var(--background-light)'
                      }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '10px' }}>{day}</div>
                      {hours.map((hour, index) => (
                        <div 
                          key={index}
                          style={{ 
                            fontSize: '14px', 
                            color: 'var(--text-medium)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <Clock size={14} />
                          {hour.start} - {hour.end}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Media Links */}
              <div>
                <h3 style={{ marginBottom: '15px' }}>Social Media</h3>
                <div style={{ display: 'flex', gap: '15px' }}>
                  {selectedProvider.socialMediaLinks?.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                      style={{ padding: '8px 15px' }}
                    >
                      <Globe size={16} style={{ marginRight: '8px' }} />
                      {social.platform}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Providers Table */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Service Providers</div>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Showing {currentProviders.length} of {filteredProviders.length} providers
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedProviders.length === currentProviders.length && currentProviders.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Provider</th>
                  <th>Contact</th>
                  <th>Skills & Experience</th>
                  <th>Performance</th>
                  <th>Verification</th>
                  <th>Status</th>
                  <th>Subscription</th>
                  <th>Last Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProviders.map(provider => (
                  <tr key={provider.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedProviders.includes(provider.id)}
                        onChange={() => handleSelectProvider(provider.id)}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div 
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: provider.profilePicture ? `url(${provider.profilePicture})` : 'var(--accent-blue)',
                            backgroundSize: 'cover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600'
                          }}
                        >
                          {!provider.profilePicture && `${provider.firstName[0]}${provider.lastName[0]}`}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                            {provider.firstName} {provider.lastName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                            ID: {provider.id} • {provider.experienceYears} years exp.
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                          {provider.email}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {provider.phone}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {provider.parish}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          {provider.skills.slice(0, 2).join(', ')}
                          {provider.skills.length > 2 && ` +${provider.skills.length - 2} more`}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {provider.experienceYears} Years Active
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                          <Star size={14} style={{ color: 'var(--accent-orange)' }} />
                          <span style={{ fontWeight: '500' }}>
                            {provider.rating > 0 ? provider.rating.toFixed(1) : 'No ratings'}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {provider.totalReviews} reviews • {provider.totalJobsCompleted} jobs
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={getVerificationBadge(provider.verificationStatus)}>
                        {provider.verificationStatus}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusBadge(provider.profileStatus)}>
                        {provider.profileStatus}
                      </span>
                    </td>
                    <td>
                      {provider.subscriptionPlan ? (
                        <div>
                          <span className={getSubscriptionBadge(provider.subscriptionPlan)}>
                            {provider.subscriptionPlan}
                          </span>
                          <div style={{ fontSize: '10px', color: 'var(--text-medium-gray)', marginTop: '2px' }}>
                            Until {new Date(provider.subscriptionEndDate!).toLocaleDateString()}
                          </div>
                        </div>
                      ) : (
                        <span className="badge badge-secondary">No plan</span>
                      )}
                    </td>
                    <td style={{ fontSize: '12px' }}>{provider.lastActive}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button 
                          className="btn-secondary" 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(provider, 'view')}
                          title="View Profile"
                        >
                          <Eye size={14} />
                        </button>
                        <button 
                          className="btn-secondary" 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(provider, 'documents')}
                          title="View Documents"
                        >
                          <FileText size={14} />
                        </button>
                        {provider.verificationStatus === 'pending' && (
                          <>
                            <button 
                              className="btn-success" 
                              style={{ padding: '6px', minWidth: 'auto' }}
                              onClick={() => handleVerificationUpdate(provider.id, 'verified')}
                              title="Approve Provider"
                            >
                              <CheckCircle size={14} />
                            </button>
                            <button 
                              className="btn-danger" 
                              style={{ padding: '6px', minWidth: 'auto' }}
                              onClick={() => handleVerificationUpdate(provider.id, 'rejected')}
                              title="Reject Provider"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        <button 
                          className="btn-secondary" 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(provider, 'edit')}
                          title="Edit Profile"
                        >
                          <Edit size={14} />
                        </button>
                        <button 
                          className={provider.profileStatus === 'suspended' ? 'btn-success' : 'btn-danger'} 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleStatusUpdate(provider.id, provider.profileStatus === 'suspended' ? 'active' : 'suspended')}
                          title={provider.profileStatus === 'suspended' ? 'Reactivate' : 'Suspend'}
                        >
                          <Shield size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            padding: '20px 0'
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button 
                className="btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedProviders.length > 0 && (
          <div className="admin-card" style={{ 
            position: 'fixed', 
            bottom: '20px', 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 1000,
            minWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{selectedProviders.length}</strong> providers selected
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-success">
                  <CheckCircle size={18} />
                  Approve Selected
                </button>
                <button className="btn-danger">
                  <XCircle size={18} />
                  Reject Selected
                </button>
                <button className="btn-secondary">
                  <Mail size={18} />
                  Send Message
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => setSelectedProviders([])}
                >
                  <XCircle size={18} />
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <ServiceProviderForm
          provider={selectedProvider || undefined}
          onSubmit={handleFormSubmit}
          onClose={() => setShowForm(false)}
        />
      )}
    </AdminLayout>
  );
};

export default ServiceProviderManagement; 